import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DollarSign, Settings, BarChart3, Building2, UserPlus,
  Users, BookOpen, Cpu, ChevronRight, ArrowRight, LayoutDashboard
} from 'lucide-react';

const DashboardTest = () => {
  const navigate = useNavigate();
  const [hoveredCard, setHoveredCard] = useState(null);

  const modules = [
    {
      id: 1,
      title: 'Sales',
      subtitle: 'Revenue & orders',
      icon: DollarSign,
      accent: '#3B82F6',
      onClick: () => navigate('/admin/sales-dashboard'),
    },
    {
      id: 2,
      title: 'Completed Job Orders',
      subtitle: 'Finished jobs log',
      icon: BookOpen,
      accent: '#22D3A5',
      onClick: () => navigate('/admin/completed-job-orders'),
    },
    {
      id: 3,
      title: 'Operations',
      subtitle: 'Field ops control',
      icon: Settings,
      accent: '#60A5FA',
      onClick: () => navigate('/admin/operations-dashboard'),
    },
    {
      id: 4,
      title: 'Commercial Client',
      subtitle: 'C&I client portal',
      icon: Building2,
      accent: '#F472B6',
      onClick: () => navigate('/admin/commercial-dashboard'),
    },
    {
      id: 5,
      title: 'Create Users',
      subtitle: 'Add team members',
      icon: UserPlus,
      accent: '#FBBF24',
      onClick: () => navigate('/admin/create-user'),
    },
    {
      id: 6,
      title: 'User Management',
      subtitle: 'Roles & access',
      icon: Users,
      accent: '#F87171',
      onClick: () => navigate('/admin/user-management'),
    },
    {
      id: 7,
      title: 'Accounts',
      subtitle: 'Finance & billing',
      icon: BarChart3,
      accent: '#34D399',
    },
    {
      id: 8,
      title: 'IOT',
      subtitle: 'NOVAA Tracker data',
      icon: Cpu,
      accent: '#FB923C',
      onClick: () => navigate('/admin/iot'),
    },
  ];

  return (
    <div style={{ padding: '14px 20px', fontFamily: "'DM Sans', sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700;9..40,800&family=DM+Mono:wght@400;500&display=swap');
        @keyframes adSlideUp {
          from { opacity:0; transform:translateY(14px); }
          to   { opacity:1; transform:translateY(0); }
        }
        .ad-card { animation: adSlideUp 0.4s both; }
        .ad-arrow { transition: transform 0.2s ease; }
        .ad-card:hover .ad-arrow { transform: translateX(4px); }
      `}</style>

      {/* ── HEADER ────────────────────────────────────── */}
      <div style={{
        background: '#0D1120',
        border: '1px solid rgba(255,255,255,0.06)',
        borderRadius: '14px',
        padding: '20px 24px 18px',
        marginBottom: '20px',
      }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: '6px',
          fontSize: '11px', fontWeight: '700', letterSpacing: '0.12em',
          textTransform: 'uppercase', color: 'rgba(255,255,255,0.25)',
          marginBottom: '10px',
        }}>
          <span>NOVAA</span>
          <ChevronRight size={12} color="rgba(255,255,255,0.12)"/>
          <span style={{ color: 'rgba(34,211,165,0.55)' }}>Admin Dashboard</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: '12px', marginBottom: '14px' }}>
          <div>
            <div style={{
              fontSize: '26px', fontWeight: '800', color: 'rgba(255,255,255,0.92)',
              letterSpacing: '-0.03em', lineHeight: 1.1,
              display: 'flex', alignItems: 'center', gap: '12px',
            }}>
              <LayoutDashboard size={24} color="#3B82F6"/>
              Dashboard
            </div>
            <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.28)', marginTop: '5px' }}>
              Select a module to get started
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{
              fontFamily: "'DM Mono', monospace",
              fontSize: '30px', fontWeight: '700', lineHeight: 1,
              background: 'linear-gradient(90deg,#3B82F6,#22D3A5)',
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
            }}>
              {modules.length}
            </div>
            <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.25)', fontWeight: '600', letterSpacing: '0.08em', textTransform: 'uppercase', marginTop: '2px' }}>
              Modules
            </div>
          </div>
        </div>
        <div style={{ height: '2px', borderRadius: '2px', background: 'linear-gradient(90deg,#3B82F6,#22D3A5,transparent)' }}/>
      </div>

      {/* ── MODULE GRID ───────────────────────────────── */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
        gap: '14px',
      }}>
        {modules.map((mod, i) => {
          const Icon = mod.icon;
          const isHov = hoveredCard === mod.id;
          const hasNav = !!mod.onClick;

          return (
            <div
              key={mod.id}
              className="ad-card"
              style={{
                animationDelay: `${i * 0.05}s`,
                background: isHov
                  ? `linear-gradient(135deg, rgba(${hexToRgb(mod.accent)},0.09) 0%, rgba(255,255,255,0.02) 100%)`
                  : '#0D1120',
                border: `1px solid ${isHov ? `${mod.accent}35` : 'rgba(255,255,255,0.06)'}`,
                borderRadius: '14px',
                padding: '24px',
                cursor: hasNav ? 'pointer' : 'default',
                transition: 'all 0.25s cubic-bezier(0.4,0,0.2,1)',
                transform: isHov ? 'translateY(-4px)' : 'none',
                boxShadow: isHov ? `0 12px 32px rgba(0,0,0,0.30), 0 0 0 1px ${mod.accent}22` : 'none',
                position: 'relative', overflow: 'hidden',
                opacity: hasNav ? 1 : 0.55,
              }}
              onMouseEnter={() => hasNav && setHoveredCard(mod.id)}
              onMouseLeave={() => setHoveredCard(null)}
              onClick={mod.onClick}
            >
              {/* Corner glow */}
              <div style={{
                position: 'absolute', top: '-40px', right: '-40px',
                width: '120px', height: '120px', borderRadius: '50%',
                background: `radial-gradient(circle, ${mod.accent}18 0%, transparent 70%)`,
                pointerEvents: 'none',
                opacity: isHov ? 1 : 0,
                transition: 'opacity 0.3s',
              }}/>

              {/* Icon box */}
              <div style={{
                width: '48px', height: '48px', borderRadius: '12px',
                background: isHov ? `${mod.accent}18` : 'rgba(255,255,255,0.05)',
                border: `1px solid ${isHov ? `${mod.accent}30` : 'rgba(255,255,255,0.07)'}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                marginBottom: '16px',
                transition: 'all 0.25s',
                transform: isHov ? 'scale(1.08)' : 'scale(1)',
              }}>
                <Icon size={22} color={isHov ? mod.accent : 'rgba(255,255,255,0.35)'}
                  style={{ transition: 'color 0.25s' }}
                />
              </div>

              {/* Text */}
              <div style={{
                fontSize: '15px', fontWeight: '700',
                color: isHov ? '#E2E8F0' : 'rgba(255,255,255,0.65)',
                marginBottom: '4px', transition: 'color 0.2s',
                letterSpacing: '-0.01em',
              }}>
                {mod.title}
              </div>
              <div style={{
                fontSize: '12px',
                color: isHov ? 'rgba(255,255,255,0.38)' : 'rgba(255,255,255,0.22)',
                transition: 'color 0.2s',
              }}>
                {mod.subtitle}
              </div>

              {/* Open module row */}
              {isHov && hasNav && (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: '5px',
                  marginTop: '16px', paddingTop: '14px',
                  borderTop: `1px solid ${mod.accent}20`,
                  animation: 'adSlideUp 0.2s ease both',
                }}>
                  <span style={{
                    fontSize: '11px', fontWeight: '700', letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    background: `linear-gradient(90deg, ${mod.accent}, #22D3A5)`,
                    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
                  }}>
                    Open Module
                  </span>
                  <ArrowRight size={12} color={mod.accent} className="ad-arrow"/>
                </div>
              )}

              {/* Disabled badge */}
              {!hasNav && (
                <div style={{
                  position: 'absolute', top: '14px', right: '14px',
                  fontSize: '9px', fontWeight: '700', letterSpacing: '0.12em',
                  textTransform: 'uppercase', color: 'rgba(255,255,255,0.20)',
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.07)',
                  padding: '3px 8px', borderRadius: '20px',
                }}>
                  Soon
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// helper — converts #RRGGBB to "R,G,B" for rgba()
function hexToRgb(hex) {
  const r = parseInt(hex.slice(1,3),16);
  const g = parseInt(hex.slice(3,5),16);
  const b = parseInt(hex.slice(5,7),16);
  return `${r},${g},${b}`;
}

export default DashboardTest;